/* eslint-disable jsx-a11y/anchor-is-valid */
import { useQuery, useReactiveVar } from '@apollo/client'
import { Close, Groups, Person } from '@mui/icons-material'
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, styled, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BucketType, BucketUserDocument, BucketUserRole } from '../../../../graphql/generated'
import { modalVar } from '../../../../providers/apollo/cache'
import useBucket from '../../hooks/useBucket'
import useBucketUpdate from '../../hooks/useBucketUpdate'

interface Fields {
  bucketName: string
}

const TitleText = styled(DialogTitle)({
  fontFamily: 'Outfit',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '13px',
  pb: 1,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
})

const RoundedButtons = styled(Button)({
  borderRadius: '80px',
  textTransform: 'none',
  padding: '10px 16px',
  width: '144px'
})

export default function BucketSettings() {
  const [fields, setFields] = useState<Fields>({ bucketName: '' })
  const modal = useReactiveVar(modalVar)
  const { mutation } = useBucketUpdate()
  const { bucket, bucketUser } = useBucket()
  const navigate = useNavigate()
  const isGroupBucket = bucket?.bucketType === BucketType.Group
  const bucketId = bucket?.id
  const ownerId = bucket?.ownerId
  const ownerQuery = useQuery(BucketUserDocument, {
    skip: !ownerId || !bucketId,
    variables: { userId: ownerId!, bucketId: bucketId! }
  })

  const ownerString =
    bucketUser?.id === ownerQuery.data?.bucketUser?.id ? 'You' : ownerQuery.data?.bucketUser?.userProfile?.email ?? ownerQuery.data?.bucketUser?.id

  const shraredWithString = bucket?.bucketUserCount === 1 ? 'You are the only member' : `Shared with ${bucket?.bucketUserCount} people`

  const onClose = () => {
    modalVar(null)
    setFields({ bucketName: '' })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFields({ ...fields, [event.target.name]: event.target.value })
  }

  const onSave = () => {
    if (!fields.bucketName || !bucket?.id) {
      return
    }

    const variables = { bucketId: bucket.id, data: { title: fields.bucketName } }
    mutation({
      variables,
      optimisticResponse: {
        bucketUpdate: {
          ...bucket,
          title: fields.bucketName
        }
      }
    })
    setFields({ bucketName: '' })
  }

  const onPressManagePermissions = () => {
    onClose()
    navigate(`/bucket/${bucketId}/manage`)
  }

  return (
    <Dialog
      open={modal === 'bucketSettings'}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: 3,
          p: 1
        }
      }}
    >
      <TitleText
        sx={{
          fontSize: 20,
          fontWeight: 'bold',
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Bucket Settings
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </TitleText>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3, mt: 1 }}>
          <TextField
            fullWidth
            label="Bucket Name"
            variant="outlined"
            size="small"
            name="bucketName"
            value={fields.bucketName}
            onChange={handleChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {/* add image icon */}
          <PersonPinOutlinedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
          <Typography variant="body2" color="text.secondary">
            Created by {ownerString}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {isGroupBucket && (
            <>
              <Groups fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
              <Typography variant="body2" color="text.secondary">
                {shraredWithString}
              </Typography>
            </>
          )}
          {!isGroupBucket && (
            <>
              <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary', fontSize: 22 }} />
              <Typography variant="body2" color="text.secondary">
                Personal
              </Typography>
            </>
          )}
        </Box>

        <Link
          component="button"
          variant="body2"
          underline="none"
          onClick={onPressManagePermissions}
          sx={{
            color: '#1C47CB',
            display: 'block',
            mb: 1,
            textAlign: 'left'
          }}
        >
          Manage Permissions
        </Link>
        {bucketUser?.userRole !== BucketUserRole.Owner && (
          <Link
            component="button"
            variant="body2"
            underline="none"
            sx={{
              color: '#1C47CB',
              display: 'block',
              textAlign: 'left'
            }}
          >
            Leave Bucket
          </Link>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2
        }}
      >
        <RoundedButtons variant="outlined" onClick={onClose}>
          Cancel
        </RoundedButtons>
        <RoundedButtons variant="contained" onClick={onSave} style={{ backgroundColor: '#1C47CB', color: '#fff' }}>
          Apply
        </RoundedButtons>
      </DialogActions>
    </Dialog>
  )
}
